import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, GripVertical, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { carouselAPI } from '../../services/api';
import { toast } from 'sonner';

const EMPTY = {
  title: 'New Slide',
  subtitle: '',
  cta: 'Shop Now',
  ctaLink: '/products',
  image: '',
  order: 0,
  active: true,
};

const CarouselAdmin = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      setSlides(await carouselAPI.getAll());
    } catch (e) {
      toast.error('Failed to load slides');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateField = (id, field, value) => {
    setSlides((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const saveSlide = async (s) => {
    setSaving((p) => ({ ...p, [s.id]: true }));
    try {
      const { id, ...payload } = s;
      await carouselAPI.update(id, payload);
      toast.success(`"${s.title}" saved`);
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving((p) => ({ ...p, [s.id]: false }));
    }
  };

  const deleteSlide = async (id) => {
    if (!window.confirm('Delete this slide?')) return;
    try {
      await carouselAPI.delete(id);
      toast.success('Slide deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const addSlide = async () => {
    try {
      const order = (slides[slides.length - 1]?.order ?? -1) + 1;
      const created = await carouselAPI.create({ ...EMPTY, order });
      setSlides((p) => [...p, created]);
      toast.success('Slide added');
    } catch (e) {
      toast.error('Failed to add slide');
    }
  };

  const toggleActive = async (s) => {
    try {
      await carouselAPI.update(s.id, { active: !s.active });
      updateField(s.id, 'active', !s.active);
    } catch (e) {
      toast.error('Failed to toggle');
    }
  };

  return (
    <div className="space-y-4" data-testid="carousel-admin">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">Hero Carousel</h2>
          <p className="text-sm text-gray-500">Slides shown at the top of the homepage. Inactive slides won't display.</p>
        </div>
        <Button onClick={addSlide} data-testid="add-slide-btn"><Plus className="h-4 w-4 mr-2" /> Add Slide</Button>
      </div>

      {loading ? (
        <p className="text-gray-500 text-center py-10">Loading…</p>
      ) : slides.length === 0 ? (
        <p className="text-gray-500 text-center py-10">No slides yet. Click "Add Slide" to create one.</p>
      ) : (
        slides.map((s) => (
          <Card key={s.id} className={!s.active ? 'opacity-60' : ''}>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1">
                  {s.image ? (
                    <img src={s.image} alt={s.title} className="w-full h-40 object-cover rounded bg-gray-100" />
                  ) : (
                    <div className="w-full h-40 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-sm">No image</div>
                  )}
                  <div className="flex gap-1 mt-2">
                    <Button size="sm" variant="outline" onClick={() => toggleActive(s)} data-testid={`slide-toggle-${s.id}`}>
                      {s.active ? <><Eye className="h-3 w-3 mr-1" /> Active</> : <><EyeOff className="h-3 w-3 mr-1" /> Hidden</>}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteSlide(s.id)} data-testid={`slide-delete-${s.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                <div className="lg:col-span-2 space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium mb-1">Title</label>
                      <Input value={s.title} onChange={(e) => updateField(s.id, 'title', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Order</label>
                      <Input type="number" value={s.order} onChange={(e) => updateField(s.id, 'order', Number(e.target.value))} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Subtitle</label>
                    <Input value={s.subtitle} onChange={(e) => updateField(s.id, 'subtitle', e.target.value)} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium mb-1">Button text</label>
                      <Input value={s.cta} onChange={(e) => updateField(s.id, 'cta', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Button link</label>
                      <Input value={s.ctaLink} onChange={(e) => updateField(s.id, 'ctaLink', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Image URL</label>
                    <Input value={s.image} onChange={(e) => updateField(s.id, 'image', e.target.value)} placeholder="https://… or /images/…" />
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" onClick={() => saveSlide(s)} disabled={saving[s.id]} data-testid={`slide-save-${s.id}`}>
                      <Save className="h-3 w-3 mr-1" />
                      {saving[s.id] ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default CarouselAdmin;
